import React from "react";
import { Link } from "react-router-dom";
import { Star, ShoppingCart } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LangContext";
import { WishlistButton } from "@/components/WishlistButton";
import { fmtPrice, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

export const ProductCard = ({ product, index = 0 }) => {
  const { add } = useCart();
  const { t } = useLang();
  const soldOut = product.stock <= 0;

  return (
    <div className="group bg-white border border-stone-200 rounded-xl overflow-hidden card-hover fade-up flex flex-col shadow-md"
      style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }} data-testid={`product-card-${product.id}`}>
      <Link to={`/product/${product.id}`} className="block img-zoom-wrap overflow-hidden aspect-square bg-stone-100 relative">
        <img src={mediaUrl(product.image)} alt={product.name} loading="lazy" className="w-full h-full object-cover img-zoom" />
        {product.featured && <span className="absolute top-3 left-3 bg-mango-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full">★ {t("featured").split(" ")[0]}</span>}
        <WishlistButton productId={product.id} className="absolute top-2.5 right-2.5 w-9 h-9 bg-white/90 backdrop-blur hover:bg-white shadow-sm" size={17} />
        {soldOut && <span className="absolute inset-0 bg-white/70 grid place-items-center text-sm font-semibold text-stone-700">{t("out_of_stock")}</span>}
      </Link>
      <div className="p-4 flex flex-col flex-1">
        <Link to={`/shop/${product.seller_id}`} className="text-[11px] uppercase tracking-wider text-stone-400 mb-1 hover:text-emerald-800 transition-colors" data-testid={`shop-link-${product.seller_id}`}>{product.seller_name}</Link>
        <Link to={`/product/${product.id}`} className="font-medium text-stone-900 leading-snug line-clamp-2 hover:text-emerald-800 transition-colors">{product.name}</Link>
        <div className="flex items-center gap-1 mt-1 text-xs text-stone-500">
          <Star className="w-3.5 h-3.5 fill-mango-600 text-mango-600" /> {product.rating?.toFixed(1)}
        </div>
        <div className="mt-auto pt-3 flex items-center justify-between gap-2">
          <span className="font-display font-extrabold text-lg text-emerald-800">{fmtPrice(product.price)}</span>
          <Button size="icon" disabled={soldOut} onClick={() => { add(product); toast.success(`${product.name}`, { description: t("add_to_cart") }); }}
            title={t("add_to_cart")} aria-label={t("add_to_cart")}
            className="rounded-full bg-emerald-800 hover:bg-emerald-900 w-10 h-10 shrink-0 shadow-sm transition-transform hover:scale-105" data-testid={`add-cart-${product.id}`}>
            <ShoppingCart className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export const ProductCardSkeleton = () => (
  <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
    <div className="aspect-square skeleton" />
    <div className="p-4 space-y-2">
      <div className="h-3 w-1/2 skeleton rounded" />
      <div className="h-4 w-3/4 skeleton rounded" />
      <div className="h-5 w-1/3 skeleton rounded mt-3" />
    </div>
  </div>
);
